import { useMemo, useState } from 'react';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer,
  XAxis, YAxis,
} from 'recharts';
import type { PostChartPoint } from '@wml-crm/shared';
import { colorForAccount } from './palette.js';
import { formatSubName } from '../lib/subName.js';

interface Props {
  points: PostChartPoint[];
  hiddenAccountIds?: Set<string>;
  modelDisplayName: string;
  onPointClick?: (p: PostChartPoint) => void;
  /** Меняется при закрытии drawer — форсит remount, чтобы hover-state не залипал */
  resetKey?: number;
}

function toEpochDay(dateStr: string): number {
  return Math.floor(new Date(dateStr).getTime() / 86_400_000);
}
function fromEpochDay(v: number): string {
  const d = new Date(v * 86_400_000);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
function formatViews(v: number): string {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1) + 'K';
  return String(v);
}
function fmtConv(x: number | null): string {
  if (x == null) return '—';
  return `1/${Number.isInteger(x) ? x : x.toFixed(1)}`;
}

interface MergedRow {
  x: number;
  /** Значения views по каждому accountId и meta-инфо по каждой точке. */
  [key: string]: number | PostChartPoint | null;
}

interface HoverState {
  point: PostChartPoint;
  cx: number;
  cy: number;
}

export function PostChart({ points, hiddenAccountIds, modelDisplayName, onPointClick, resetKey = 0 }: Props) {
  const hidden = hiddenAccountIds ?? new Set<string>();
  const [hover, setHover] = useState<HoverState | null>(null);

  const { data, accounts } = useMemo(() => {
    const accMap = new Map<string, string>(); // accountId → slaveName
    for (const p of points) if (!accMap.has(p.accountId)) accMap.set(p.accountId, p.slaveName);

    const byDay = new Map<number, MergedRow>();
    for (const p of points) {
      const x = toEpochDay(p.lastReportDate);
      let row = byDay.get(x);
      if (!row) {
        row = { x };
        byDay.set(x, row);
      }
      row[`v_${p.accountId}`] = p.views;
      row[`m_${p.accountId}`] = p;
    }
    const rows = Array.from(byDay.values()).sort((a, b) => a.x - b.x);
    return {
      data: rows,
      accounts: Array.from(accMap.entries()).map(([id, slave]) => ({ id, slave })),
    };
  }, [points]);

  const visibleAccounts = accounts.filter((a) => !hidden.has(a.id));
  // Адаптивные размеры точек/линий — чем больше видимых линий, тем мельче.
  const n = visibleAccounts.length;
  const dotR = n >= 12 ? 2 : n >= 8 ? 2.5 : n >= 5 ? 3.5 : 5;
  const dotStroke = n >= 8 ? 1 : 2;
  const activeR = n >= 12 ? 5 : n >= 8 ? 6 : 7;
  const lineW = n >= 12 ? 1.4 : n >= 8 ? 1.8 : 2.5;

  const domain: [number, number] | undefined = data.length
    ? [data[0]!.x - 1, data[data.length - 1]!.x + 1]
    : undefined;

  // Позиционирование tooltip'а: сдвиг вправо/влево от точки, чтобы не вылезало
  const tooltipStyle = hover
    ? {
        position: 'absolute' as const,
        left: hover.cx + 14,
        top: Math.max(8, hover.cy - 40),
        pointerEvents: 'none' as const,
        zIndex: 10,
      }
    : undefined;

  return (
    <div
      style={{ width: '100%', height: 460, position: 'relative' }}
      key={resetKey}
      onMouseLeave={() => setHover(null)}
    >
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 20, right: 30, bottom: 10, left: 10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="4 4" vertical={false} />
          <XAxis
            type="number"
            dataKey="x"
            domain={domain}
            tickFormatter={fromEpochDay}
            stroke="var(--text-muted)"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            type="number"
            tickFormatter={formatViews}
            stroke="var(--text-muted)"
            tick={{ fontSize: 12 }}
            width={60}
          />
          {visibleAccounts.map((acc) => {
            const color = colorForAccount(acc.id);
            return (
              <Line
                key={acc.id}
                type="monotone"
                dataKey={`v_${acc.id}`}
                name={acc.slave}
                stroke={color}
                strokeWidth={lineW}
                dot={(dotProps: { cx?: number; cy?: number; payload?: MergedRow; index?: number }) => {
                  const { cx, cy, payload } = dotProps;
                  const meta = payload?.[`m_${acc.id}`];
                  if (cx == null || cy == null || !meta || typeof meta !== 'object') {
                    return <g key={`empty-${acc.id}-${dotProps.index ?? 0}`} />;
                  }
                  const p = meta as PostChartPoint;
                  const isActive = hover?.point.postId === p.postId;
                  return (
                    <circle
                      key={p.postId}
                      cx={cx}
                      cy={cy}
                      r={isActive ? activeR : dotR}
                      fill={color}
                      stroke="var(--bg-elev)"
                      strokeWidth={isActive ? Math.max(2, dotStroke + 1) : dotStroke}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHover({ point: p, cx, cy })}
                      onMouseLeave={() => setHover((h) => (h?.point.postId === p.postId ? null : h))}
                      onClick={() => onPointClick?.(p)}
                    />
                  );
                }}
                activeDot={false}
                connectNulls
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      {hover && tooltipStyle && (
        <div className="tooltip-card" style={tooltipStyle}>
          <div className="tooltip-name">
            {modelDisplayName}
            {(() => {
              const sub = formatSubName(hover.point.slaveName, modelDisplayName);
              return sub ? <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}> · {sub}</span> : null;
            })()}
          </div>
          <div className="tooltip-sub">
            Пост {new Date(hover.point.postDate).toLocaleDateString('ru-RU')} → отчёт {new Date(hover.point.lastReportDate).toLocaleDateString('ru-RU')}
          </div>
          <div className="tooltip-grid">
            <span>Охват</span><b>{formatViews(hover.point.views)}</b>
            {hover.point.retweets > 0 && (<><span>Ретвитов</span><b>{hover.point.retweets.toLocaleString('ru-RU')}</b></>)}
          </div>
          <div className="tooltip-conv">
            Конверт: <b>{fmtConv(hover.point.conversionDay1)}</b>
            {hover.point.daysInWork > 1 && (
              <>
                {' · '}день-2 <b>{fmtConv(hover.point.conversionDay2)}</b>
                {hover.point.daysInWork > 2 && <> · средний <b>{fmtConv(hover.point.conversionAvg)}</b></>}
              </>
            )}
          </div>
          <div className="tooltip-days">Дней в работе: {hover.point.daysInWork}</div>
        </div>
      )}
    </div>
  );
}
